import { z } from "zod";
import {
  emailField,
  confirmEmailField,
  firstNameField,
  lastNameField,
  optionalMessageField,
} from "@core/schemas/fields";
import { withEmailConfirmation } from "@core/schemas/refinements";
import { useForm } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";
import { Type } from "@themes/pollinator/ui/Type";

export const contactFormSchema = withEmailConfirmation(
  z.object({
    firstName: firstNameField,
    lastName: lastNameField,
    email: emailField,
    confirmEmail: confirmEmailField,
    message: optionalMessageField,
  })
);

export type ContactFormData = z.infer<typeof contactFormSchema>;

export type Props = {
  /** Called when the form is submitted with valid data */
  onSubmit: (data: ContactFormData) => Promise<void>;
  /** Custom class name for the form container */
  className?: string;
};

/**
 * A contact form for users to send messages.
 * This is a pure presentational component - all business logic (API calls,
 * event tracking) should be handled by the parent.
 *
 * @example
 * <MarketingContactForm
 *   onSubmit={async (data) => {
 *     await apiRequest("/api/contact", "POST", data);
 *   }}
 * />
 */
export function MarketingContactForm({ onSubmit, className }: Props) {
  const { form, handleSubmit } = useForm({
    schema: contactFormSchema,
    onSubmit,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      message: "",
    },
  });

  const { isSubmitting, isValid, isSubmitSuccessful } = form.formState;

  if (isSubmitSuccessful) {
    return (
      <div className="flex flex-col border border-brand-800 rounded-lg p-6 gap-2">
        <Type variant="h4">Thank you for your message!</Type>
        <Type variant="body2">We will get back to you shortly.</Type>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Form form={form} className={className} innerClassName="justify-center">
        <FormField
          name="firstName"
          label="First Name"
          component="Input"
          placeholder="Enter your first name"
          required
        />
        <FormField
          name="lastName"
          label="Last Name"
          component="Input"
          placeholder="Enter your last name"
          required
        />
        <FormField
          name="email"
          label="Email"
          component="Input"
          placeholder="Enter your email"
          autoComplete="email"
          required
        />
        <FormField
          name="confirmEmail"
          label="Email Confirmation"
          component="Input"
          placeholder="Confirm your email"
          required
        />
        <FormField
          name="message"
          label="Message"
          component="Textarea"
          placeholder="Your message"
        />
      </Form>
      <ActionButtonsGroup
        onPrimaryClick={handleSubmit}
        primaryButtonText="Submit"
        primaryButtonProps={{
          disabled: isSubmitting,
          visuallyDisabled: !isSubmitting && !isValid,
        }}
      />
    </div>
  );
}
